/**
 * The VISIBLE map, when the results sheet rests over the pane.
 *
 * On the mobile canvas surface the map pane fills the surface and the results
 * drawer (`MobileSheet`) rests over its bottom `SNAP_FRACTION[RESTING_POSITION]`
 * of the viewport. The server centres the map on the bounds the hub was opened
 * on, and Google centres that point in the PANE — so the centre of the area a
 * reader searched, and the pin the server put there, sat under the drawer's
 * handle: `elementFromPoint` at the pin answered the drawer, and no tap could
 * reach it. Measured on the first staging smoke of the seeded map subjects
 * (2026-09-14): the pin at (197, 410) of a 393×852 viewport, covered by the
 * `cursor-grab` handle of a sheet whose top edge is at 852 × (1 − 0.55) = 383.
 *
 * The fix is ONE concept, derived from the sheet's own SSOT and applied once:
 * the map's centre of attention is the centre of the pane's VISIBLE part, i.e.
 * the pane minus the strip the resting sheet covers. Google Maps carries no
 * persistent padding, so the centre is moved DOWN by half that strip — the
 * point the server centred then renders at the visible centre. The shift is a
 * pixel distance on the Mercator plane at the map's own zoom, which is why
 * `shiftLatByPixels` is here as pure arithmetic: it is what `panBy` would do
 * without `panBy`'s animation (a pin measured mid-animation is a flake), and it
 * is provable in the PR lane with no map.
 *
 * What this deliberately does NOT do: follow the sheet as the reader drags it.
 * The resting rail is the product's own choice of what the page shows first;
 * a reader's own drag is theirs, and re-centring under their thumb would be
 * the map fighting them.
 */

import { RESTING_POSITION, SNAP_FRACTION } from './results-sheet-state';

/** Google's world tile edge at zoom 0, in CSS px — the Mercator plane's unit. */
const WORLD_TILE_PX = 256;

/**
 * The strip of the map pane the RESTING sheet covers, in px: the part of the
 * pane below the sheet's top edge. `0` when the pane ends above it.
 */
export function sheetHiddenStripPx(paneBottomPx: number, viewportHeightPx: number): number {
  const sheetTopPx = viewportHeightPx * (1 - SNAP_FRACTION[RESTING_POSITION]);
  return Math.max(0, paneBottomPx - sheetTopPx);
}

/**
 * How far DOWN (in px) the map's centre moves so the point Google would have
 * centred in the whole pane renders at the centre of the visible part.
 */
export function visibleCentreShiftPx(hiddenStripPx: number): number {
  return hiddenStripPx / 2;
}

function worldPx(zoom: number): number {
  return WORLD_TILE_PX * 2 ** zoom;
}

/** Mercator y of a latitude at `zoom`, in px from the top of the world. */
function mercatorY(latDeg: number, zoom: number): number {
  const s = Math.sin((latDeg * Math.PI) / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * worldPx(zoom);
}

/** The latitude at Mercator y (px from the top of the world) at `zoom`. */
function latFromMercatorY(y: number, zoom: number): number {
  const n = Math.PI - (2 * Math.PI * y) / worldPx(zoom);
  return (180 / Math.PI) * Math.atan(Math.sinh(n));
}

/**
 * The latitude `dyPx` screen pixels SOUTH of `latDeg` at `zoom` (negative
 * `dyPx` goes north). Pure Mercator, no map instance: the same arithmetic
 * Google performs for `panBy`, minus the animation.
 */
export function shiftLatByPixels(latDeg: number, zoom: number, dyPx: number): number {
  return latFromMercatorY(mercatorY(latDeg, zoom) + dyPx, zoom);
}
